import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { QuotaBar } from "../components/allowance/QuotaBar";
import { AssignAllowance } from "../components/holders/AssignAllowance";
import { EditHolder } from "../components/holders/EditHolder";
import { useHolders } from "../components/holders/HolderSelect";
import { PageHeader } from "../components/shell/PageHeader";
import { ErrorNotice } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import { Datum, Metric, MicroLabel, SectionTitle } from "../components/ui/Text";
import { get } from "../lib/api";
import { count, dateOnly, kwh, percent, timestamp } from "../lib/format";
import type { HolderDetail as Detail } from "../lib/types";

/**
 * One holder: the pool it draws on, the policy it sets (if any), what it holds,
 * and its place in the tree.
 *
 * The figure is the *pool's*: when the governing policy is assigned higher up,
 * this page shows the same consumption as its owner's page and every sibling's,
 * and says whose it is. Committed and in-flight energy stay separate (spec 6.3).
 */
export function HolderDetail() {
  const { holderId = "" } = useParams();
  const holders = useHolders();

  const detail = useQuery({
    queryKey: ["holder", holderId] as const,
    queryFn: () => get<Detail>(`/holders/${encodeURIComponent(holderId)}`),
    enabled: holderId !== "",
  });

  if (detail.isError) {
    return (
      <>
        <PageHeader eyebrow="Holder" title="Not available" />
        <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
        <BackLink />
      </>
    );
  }

  const row = detail.data;
  const period = row?.currentPeriod ?? null;
  const all = holders.data ?? [];
  const self = all.find((holder) => holder.id === holderId);
  const parent = all.find((holder) => holder.id === row?.parentHolderId);
  const members = all
    .filter((holder) => holder.parentHolderId === holderId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const inherited = row?.allowance != null && row.allowance.holder.id !== row.id;

  return (
    <>
      <PageHeader
        eyebrow={row === undefined ? "Holder" : row.isSystem ? "System group" : row.type}
        title={row?.name ?? "Holder"}
        metrics={
          period === null ? undefined : (
            <>
              <Metric
                label="Committed"
                value={kwh(period.committedWh)}
                unit="kWh"
                hint={`${period.closedSessionCount} closed session${period.closedSessionCount === 1 ? "" : "s"}`}
              />
              <Metric
                label="In flight"
                value={kwh(period.inFlightWh)}
                unit="kWh"
                hint="live estimate, not billable"
                tone="muted"
              />
              <Metric
                label="Of quota"
                value={percent(period.consumedPct)}
                hint={`${kwh(period.quotaWh)} kWh, shared`}
                tone={(period.consumedPct ?? 0) >= 100 ? "signal" : "ink"}
              />
            </>
          )
        }
        {...(row?.isSystem ? { actions: <Pill tone="idle">default for new cards</Pill> } : {})}
      />

      {row === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <>
          <section className="flex flex-col gap-4">
            <SectionTitle>Allowance</SectionTitle>
            {row.allowance === null ? (
              <p className="m-0 max-w-prose text-body text-ink-muted">
                No policy governs this holder or any group above it. Its cards are tracked but never
                flagged.
              </p>
            ) : (
              <>
                <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
                  <Datum label="Policy">{row.allowance.policy.name}</Datum>
                  <Datum label="Set on">
                    {inherited ? (
                      <HolderLink id={row.allowance.holder.id} name={row.allowance.holder.name} />
                    ) : (
                      "This holder"
                    )}
                  </Datum>
                  <Datum label="Quota">{kwh(row.allowance.policy.quotaWh)} kWh</Datum>
                  <Datum label="Period type">
                    {row.allowance.policy.periodType.replace(/_/g, " ")}
                  </Datum>
                  <Datum label="Assigned from">
                    {dateOnly(row.allowance.policy.effectiveFrom, ZONE)}
                  </Datum>
                  <Datum label="Warn at">
                    {row.allowance.policy.warnThresholds.map((value) => `${value}%`).join(", ")}
                  </Datum>
                  <Datum label="Concurrent sessions">
                    {row.allowance.policy.allowConcurrentTx ? "Allowed" : "One at a time"}
                  </Datum>
                </dl>
                {period === null ? null : (
                  <>
                    <QuotaBar
                      committedWh={period.committedWh}
                      inFlightWh={period.inFlightWh}
                      quotaWh={period.quotaWh}
                      thresholds={row.allowance.policy.warnThresholds}
                    />
                    <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
                      <Datum label="Period">{period.label}</Datum>
                      <Datum label="Opened">{timestamp(period.periodStart, ZONE)}</Datum>
                      <Datum label="Closes">{timestamp(period.periodEnd, ZONE)}</Datum>
                      <Datum label="Closed sessions">{period.closedSessionCount}</Datum>
                    </dl>
                    <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
                      Times shown in UTC. Period boundaries are drawn in the depot's timezone.
                      {inherited
                        ? ` This is ${row.allowance.holder.name}'s pool, shared with everything under it that has no policy of its own.`
                        : ""}
                    </p>
                  </>
                )}
              </>
            )}
            {/* Keyed on the holder: a half-confirmed removal must never survive a
                navigation to another holder's page. */}
            <AssignAllowance key={row.id} holderId={row.id} allowance={row.allowance} />
          </section>

          <section className="flex flex-col gap-4">
            <SectionTitle
              action={
                <Link
                  to={`/identities?holderId=${encodeURIComponent(row.id)}`}
                  className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
                >
                  Its cards
                </Link>
              }
            >
              Holds
            </SectionTitle>
            <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-4">
              <Datum label="Cards">{count(self?.identityCount)}</Datum>
              <Datum label="Holders">{count(self?.childCount)}</Datum>
              <Datum label="Inside">
                {parent === undefined ? "Top level" : <HolderLink id={parent.id} name={parent.name} />}
              </Datum>
              <Datum label="Reference">{row.externalRef ?? "—"}</Datum>
            </dl>
            {members.length === 0 ? null : (
              <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-2 p-0">
                {members.map((member) => (
                  <li key={member.id}>
                    <HolderLink id={member.id} name={member.name} />
                    <span className="text-micro tracking-normal normal-case text-ink-faint">
                      {" "}
                      · {member.type}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <SectionTitle>Details</SectionTitle>
            {/* Keyed on the holder only, so a refetch keeps drafts and notices,
                while another holder's page starts clean. */}
            <EditHolder key={row.id} holder={row} />
          </section>

          <BackLink />
        </>
      )}
    </>
  );
}

const ZONE = "UTC";

function HolderLink({ id, name }: { readonly id: string; readonly name: string }) {
  return (
    <Link
      to={`/holders/${encodeURIComponent(id)}`}
      className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      {name}
    </Link>
  );
}

function BackLink() {
  return (
    <Link
      to="/holders"
      className="self-start text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
    >
      Back to holders
    </Link>
  );
}
